import { authPagesPaths } from './navigationPages';

/**
 * Resolves a notification's destination KEY to a route in this app.
 *
 * The backend stores a symbolic key (`addresses_monitoring`), never a URL. It used to
 * store the path itself, which froze this app's route structure into a foreign
 * database — every historical notification row kept a copy, so no route could be
 * reorganised without silently 404ing links already sent to customers. Routes belong
 * to the app that serves them, so the mapping lives here.
 *
 * The producing map is `Notificationqueue::LINK_KEYS` in amlbot. The two are edited
 * together: a key added there without an entry here resolves to null, which renders as
 * no link rather than a broken one.
 *
 * Values come from `authPagesPaths` rather than being re-typed, so a route rename is
 * picked up here automatically instead of drifting.
 */
export const NOTIFICATION_LINK_PATHS: Record<string, string> = {
  tariffs: authPagesPaths.TARIFFS_PAGE_PATH,
  profile: authPagesPaths.PROFILE_PAGE_PATH,
  compliance_dashboard: authPagesPaths.COMPLIANCE_DASHBOARD_PAGE_PATH,
  addresses_monitoring:
    authPagesPaths.COMPLIANCE_DASHBOARD_ADDRESSES_MONITORING_PAGE_PATH,
};

/**
 * A key this app does not know resolves to null, not to a guess.
 *
 * Two cases reach that branch and both should produce no link: a key from a newer
 * backend this build predates, and a LEGACY row written before the key change, whose
 * `link` holds a literal path like `/compliance-dashboard/addresses-monitoring`. A
 * stored path is deliberately NOT honoured — accepting one would keep alive the
 * coupling this indirection exists to remove, and personal notifications age out of
 * the retention window anyway.
 */
export const resolveNotificationLinkKey = (
  key: string | null | undefined,
): string | null => {
  if (!key) return null;

  // hasOwnProperty, not a bare lookup. A plain object inherits from Object.prototype,
  // so `NOTIFICATION_LINK_PATHS['constructor']` returns a FUNCTION — truthy, so it
  // sails through the `||` below and this returns a constructor where the caller
  // expects a path. Same for 'toString' and '__proto__'. The key arrives from the
  // backend, so it is not ours to assume well-formed.
  if (!Object.prototype.hasOwnProperty.call(NOTIFICATION_LINK_PATHS, key)) {
    return null;
  }

  return NOTIFICATION_LINK_PATHS[key] || null;
};
